import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEntity } from '../contexts/EntityContext';
import { api, ApiError } from '../lib/api';
import { useToast } from '../components/ui/Toast';
import Spinner from '../components/ui/Spinner';

type TransactionType = 'exchange' | 'expense' | 'deposit' | 'withdrawal' | 'transfer';

// Base schema for common fields
const baseSchema = z.object({
  comment: z.string().optional(),
  clientId: z.string().optional(),
});

// Exchange transaction schema
const exchangeSchema = baseSchema.extend({
  type: z.literal('exchange'),
  amountIn: z.string().min(1, 'Amount in is required').refine(
    (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    'Must be a positive number'
  ),
  currencyIn: z.string().min(1, 'Currency is required'),
  walletInId: z.string().min(1, 'Wallet is required'),
  amountOut: z.string().min(1, 'Amount out is required').refine(
    (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    'Must be a positive number'
  ),
  currencyOut: z.string().min(1, 'Currency is required'),
  walletOutId: z.string().min(1, 'Wallet is required'),
  exchangeRate: z.string().optional(),
  oracleRate: z.string().optional(),
  status: z.enum(['open', 'closed']),
  expectedAmount: z.string().optional(),
  expectedPaymentDate: z.string().optional(),
});

// Expense transaction schema
const expenseSchema = baseSchema.extend({
  type: z.literal('expense'),
  amount: z.string().min(1, 'Amount is required').refine(
    (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    'Must be a positive number'
  ),
  currency: z.string().min(1, 'Currency is required'),
  walletId: z.string().min(1, 'Wallet is required'),
  category: z.string().min(1, 'Category is required'),
});

// Simple transaction schema (deposit/withdrawal)
const simpleSchema = baseSchema.extend({
  type: z.enum(['deposit', 'withdrawal']),
  amount: z.string().min(1, 'Amount is required').refine(
    (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    'Must be a positive number'
  ),
  currency: z.string().min(1, 'Currency is required'),
  walletId: z.string().min(1, 'Wallet is required'),
});

// Transfer transaction schema
const transferSchema = baseSchema.extend({
  type: z.literal('transfer'),
  amount: z.string().min(1, 'Amount is required').refine(
    (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    'Must be a positive number'
  ),
  currency: z.string().min(1, 'Currency is required'),
  walletFromId: z.string().min(1, 'From wallet is required'),
  walletToId: z.string().min(1, 'To wallet is required'),
});

// Get schema for specific type
function getSchemaForType(type: TransactionType) {
  switch (type) {
    case 'exchange':
      return exchangeSchema;
    case 'expense':
      return expenseSchema;
    case 'transfer':
      return transferSchema;
    default:
      return simpleSchema;
  }
}

export default function NewTransactionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentEntity } = useEntity();
  const { success, error: showError } = useToast();
  const [type, setType] = useState<TransactionType>('exchange');

  const { data: wallets, isLoading: walletsLoading } = useQuery({
    queryKey: ['wallets', currentEntity?.id],
    queryFn: () => api.admin.wallets.list(currentEntity!.id),
    enabled: !!currentEntity,
  });

  // For transfers, load ALL entity wallets (bypasses cash desk restriction)
  const { data: allEntityWallets } = useQuery({
    queryKey: ['wallets-all', currentEntity?.id],
    queryFn: async () => {
      const searchParams = new URLSearchParams({ allWallets: 'true' });
      const res = await fetch(`/api/admin/wallets?${searchParams}`, {
        credentials: 'include',
        headers: { 'X-Entity-Id': currentEntity!.id },
      });
      if (!res.ok) throw new Error('Failed to load wallets');
      return res.json() as Promise<{ data: any[] }>;
    },
    enabled: !!currentEntity && type === 'transfer',
  });

  const { data: categories } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const res = await fetch('/api/admin/categories', { credentials: 'include' });
      return res.json();
    },
  });

  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.admin.currencies.list(),
  });

  const { register, handleSubmit, formState: { errors }, watch, reset } = useForm<Record<string, unknown>>({
    // Dynamic schema per type; cast needed for zodResolver with union types
     
    resolver: zodResolver(getSchemaForType(type) as any),
    defaultValues: {
      type,
      status: 'closed',
    },
  });

  const handleTypeChange = (newType: TransactionType) => {
    setType(newType);
    reset({
      type: newType,
      status: 'closed',
    } as any);
  };

  const mutation = useMutation({
    mutationFn: (data: any) => api.transactions.create(currentEntity!.id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      success(`${type.charAt(0).toUpperCase() + type.slice(1)} transaction created`);
      navigate(`/transactions/${result.data.id}`);
    },
    onError: (err: ApiError) => {
      showError(err.message);
    },
  });

  const onSubmit = (data: Record<string, any>) => {
    // Clean up optional fields
    const cleanData: any = { ...data };
    if (cleanData.exchangeRate === '') delete cleanData.exchangeRate;
    if (cleanData.oracleRate === '') delete cleanData.oracleRate;
    if (cleanData.expectedAmount === '') delete cleanData.expectedAmount;
    if (cleanData.expectedPaymentDate === '') delete cleanData.expectedPaymentDate;
    if (cleanData.comment === '') delete cleanData.comment;
    if (cleanData.clientId === '') delete cleanData.clientId;

    // Set status for non-exchange types
    if (type !== 'exchange') {
      cleanData.status = 'closed';
    }

    mutation.mutate(cleanData);
  };

  if (!currentEntity) return null;

  const walletList = wallets?.data || [];
  const currencyList = currencies?.data || [];

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Link to="/transactions" className="btn btn-secondary">
            &larr; Back
          </Link>
          <h1 className="page-title">New Transaction</h1>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 700 }}>
        {walletsLoading ? (
          <div className="flex items-center gap-2 text-muted">
            <Spinner size="sm" /> Loading...
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group">
              <label className="form-label">Transaction Type</label>
              <div className="type-selector">
                {(['exchange', 'expense', 'deposit', 'withdrawal', 'transfer'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`type-btn ${type === t ? 'active' : ''}`}
                    onClick={() => handleTypeChange(t)}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {type === 'exchange' && (
              <ExchangeForm
                register={register}
                errors={errors}
                watch={watch}
                wallets={walletList}
                currencies={currencyList}
              />
            )}

            {type === 'expense' && (
              <ExpenseForm
                register={register}
                errors={errors}
                watch={watch}
                wallets={walletList}
                currencies={currencyList}
                categories={categories?.data || []}
              />
            )}

            {(type === 'deposit' || type === 'withdrawal') && (
              <SimpleForm
                register={register}
                errors={errors}
                watch={watch}
                wallets={walletList}
                currencies={currencyList}
              />
            )}

            {type === 'transfer' && (
              <TransferForm
                register={register}
                errors={errors}
                watch={watch}
                wallets={allEntityWallets?.data || walletList}
                currencies={currencyList}
              />
            )}

            <div className="form-divider" />

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Client ID (optional)</label>
                <input
                  {...register('clientId')}
                  type="text"
                  className="form-input"
                  placeholder="e.g., client reference"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Comment (optional)</label>
              <textarea {...register('comment')} className="form-input" rows={2} />
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <>
                    <Spinner size="sm" /> Creating...
                  </>
                ) : (
                  'Create Transaction'
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      <style>{`
        .type-selector {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .type-btn {
          padding: 8px 16px;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          color: var(--color-text);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .type-btn:hover {
          border-color: var(--color-primary);
          background: var(--color-surface-hover);
        }
        .type-btn.active {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
        }
        .form-divider {
          border-top: 1px solid var(--color-border);
          margin: 24px 0;
        }
        .form-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
        }
        .form-section-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--color-text-muted);
          margin-bottom: 16px;
        }
        .input-with-currency {
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }
        .input-with-currency .input-field {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .input-with-currency .input-field input,
        .input-with-currency .input-field select {
          width: 100%;
        }
        .input-with-currency .currency-field {
          flex: 0 0 100px;
        }
        .field-error {
          color: var(--color-error);
          font-size: 12px;
          margin-top: 4px;
        }
        .form-input.error {
          border-color: var(--color-error);
        }
      `}</style>
    </div>
  );
}

interface FormProps {
  register: any;
  errors: any;
  watch: any;
  wallets: any[];
  currencies: any[];
  categories?: any[];
}

function ExchangeForm({ register, errors, watch, wallets, currencies }: FormProps) {
  const currencyIn = watch('currencyIn') || '';
  const currencyOut = watch('currencyOut') || '';
  const status = watch('status') || 'closed';

  return (
    <>
      <div className="form-section-title">Money In (Client gives)</div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Amount In *</label>
          <div className="input-with-currency">
            <div className="input-field">
              <input
                {...register('amountIn')}
                type="text"
                className={`form-input ${errors.amountIn ? 'error' : ''}`}
                placeholder="0.00"
              />
              {errors.amountIn && <span className="field-error">{errors.amountIn.message as string}</span>}
            </div>
            <div className="input-field currency-field">
              <select
                {...register('currencyIn')}
                className={`form-input ${errors.currencyIn ? 'error' : ''}`}
              >
                <option value="">Currency</option>
                {currencies.map((c: any) => (
                  <option key={c.id} value={c.code}>{c.code}</option>
                ))}
              </select>
              {errors.currencyIn && <span className="field-error">{errors.currencyIn.message as string}</span>}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Wallet In *</label>
          <select
            {...register('walletInId')}
            className={`form-input ${errors.walletInId ? 'error' : ''}`}
          >
            <option value="">Select wallet...</option>
            {wallets
              .filter((w: any) => !currencyIn || w.currencyCode === currencyIn)
              .map((w: any) => (
                <option key={w.id} value={w.id}>{w.name} ({w.currencyCode})</option>
              ))}
          </select>
          {errors.walletInId && <span className="field-error">{errors.walletInId.message as string}</span>}
        </div>
      </div>

      <div className="form-section-title">Money Out (Client receives)</div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Amount Out *</label>
          <div className="input-with-currency">
            <div className="input-field">
              <input
                {...register('amountOut')}
                type="text"
                className={`form-input ${errors.amountOut ? 'error' : ''}`}
                placeholder="0.00"
              />
              {errors.amountOut && <span className="field-error">{errors.amountOut.message as string}</span>}
            </div>
            <div className="input-field currency-field">
              <select
                {...register('currencyOut')}
                className={`form-input ${errors.currencyOut ? 'error' : ''}`}
              >
                <option value="">Currency</option>
                {currencies.map((c: any) => (
                  <option key={c.id} value={c.code}>{c.code}</option>
                ))}
              </select>
              {errors.currencyOut && <span className="field-error">{errors.currencyOut.message as string}</span>}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Wallet Out *</label>
          <select
            {...register('walletOutId')}
            className={`form-input ${errors.walletOutId ? 'error' : ''}`}
          >
            <option value="">Select wallet...</option>
            {wallets
              .filter((w: any) => !currencyOut || w.currencyCode === currencyOut)
              .map((w: any) => (
                <option key={w.id} value={w.id}>{w.name} ({w.currencyCode})</option>
              ))}
          </select>
          {errors.walletOutId && <span className="field-error">{errors.walletOutId.message as string}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Exchange Rate (optional)</label>
          <input
            {...register('exchangeRate')}
            type="text"
            className="form-input"
            placeholder="e.g., 1.085"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Oracle Rate (optional)</label>
          <input
            {...register('oracleRate')}
            type="text"
            className="form-input"
            placeholder="Reference rate"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Status</label>
          <select {...register('status')} className="form-input">
            <option value="closed">Closed (Paid in full)</option>
            <option value="open">Open (Has debt)</option>
          </select>
        </div>
        {status === 'open' && (
          <div className="form-group">
            <label className="form-label">Expected Amount</label>
            <input
              {...register('expectedAmount')}
              type="text"
              className="form-input"
              placeholder="Amount client should pay"
            />
          </div>
        )}
      </div>

      {status === 'open' && (
        <div className="form-group">
          <label className="form-label">Expected Payment Date (optional)</label>
          <input {...register('expectedPaymentDate')} type="date" className="form-input" />
          <small className="text-muted">When client is expected to pay</small>
        </div>
      )}
    </>
  );
}

function ExpenseForm({ register, errors, watch, wallets, currencies, categories }: FormProps) {
  const currency = watch('currency') || '';

  return (
    <>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Amount *</label>
          <div className="input-with-currency">
            <div className="input-field">
              <input
                {...register('amount')}
                type="text"
                className={`form-input ${errors.amount ? 'error' : ''}`}
                placeholder="0.00"
              />
              {errors.amount && <span className="field-error">{errors.amount.message as string}</span>}
            </div>
            <div className="input-field currency-field">
              <select
                {...register('currency')}
                className={`form-input ${errors.currency ? 'error' : ''}`}
              >
                <option value="">Currency</option>
                {currencies.map((c: any) => (
                  <option key={c.id} value={c.code}>{c.code}</option>
                ))}
              </select>
              {errors.currency && <span className="field-error">{errors.currency.message as string}</span>}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Wallet *</label>
          <select
            {...register('walletId')}
            className={`form-input ${errors.walletId ? 'error' : ''}`}
          >
            <option value="">Select wallet...</option>
            {wallets
              .filter((w: any) => !currency || w.currencyCode === currency)
              .map((w: any) => (
                <option key={w.id} value={w.id}>{w.name} ({w.currencyCode})</option>
              ))}
          </select>
          {errors.walletId && <span className="field-error">{errors.walletId.message as string}</span>}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Category *</label>
        <input
          {...register('category')}
          type="text"
          className={`form-input ${errors.category ? 'error' : ''}`}
          list="categories"
          placeholder="e.g., Rent, Utilities"
        />
        <datalist id="categories">
          {categories?.map((c: any) => <option key={c.id} value={c.name} />)}
        </datalist>
        {errors.category && <span className="field-error">{errors.category.message as string}</span>}
      </div>
    </>
  );
}

function SimpleForm({ register, errors, watch, wallets, currencies }: FormProps) {
  const currency = watch('currency') || '';

  return (
    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Amount *</label>
        <div className="input-with-currency">
          <div className="input-field">
            <input
              {...register('amount')}
              type="text"
              className={`form-input ${errors.amount ? 'error' : ''}`}
              placeholder="0.00"
            />
            {errors.amount && <span className="field-error">{errors.amount.message as string}</span>}
          </div>
          <div className="input-field currency-field">
            <select
              {...register('currency')}
              className={`form-input ${errors.currency ? 'error' : ''}`}
            >
              <option value="">Currency</option>
              {currencies.map((c: any) => (
                <option key={c.id} value={c.code}>{c.code}</option>
              ))}
            </select>
            {errors.currency && <span className="field-error">{errors.currency.message as string}</span>}
          </div>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Wallet *</label>
        <select
          {...register('walletId')}
          className={`form-input ${errors.walletId ? 'error' : ''}`}
        >
          <option value="">Select wallet...</option>
          {wallets
            .filter((w: any) => !currency || w.currencyCode === currency)
            .map((w: any) => (
              <option key={w.id} value={w.id}>{w.name} ({w.currencyCode})</option>
            ))}
        </select>
        {errors.walletId && <span className="field-error">{errors.walletId.message as string}</span>}
      </div>
    </div>
  );
}

// Currencies treated as equivalent for transfer wallet filtering
const TRANSFER_STABLECOIN_GROUP = ['USDT', 'USDC'];

function getCompatibleCurrencies(currency: string): string[] {
  const upper = currency.toUpperCase();
  if (TRANSFER_STABLECOIN_GROUP.includes(upper)) {
    return TRANSFER_STABLECOIN_GROUP;
  }
  return [upper];
}

function TransferForm({ register, errors, watch, wallets, currencies }: FormProps) {
  const currency = watch('currency') || '';

  // Wallets compatible with the selected currency (USDT ↔ USDC treated as same group)
  const compatibleCodes = currency ? getCompatibleCurrencies(currency) : null;
  const filteredWallets = compatibleCodes
    ? wallets.filter((w: any) => compatibleCodes.includes(w.currencyCode?.toUpperCase()))
    : wallets;

  return (
    <>
      <div style={{
        background: 'var(--color-surface1, #e6e9ef)',
        border: '1px solid var(--color-blue, #1e66f5)',
        borderRadius: '8px',
        padding: '10px 14px',
        marginBottom: '16px',
        fontSize: '0.85rem',
        color: 'var(--color-text)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <span style={{ fontSize: '1.1rem' }}>ℹ️</span>
        <span>Трансферы позволяют переводить средства между <strong>любыми</strong> кошельками всех групп, при условии совпадения валюты.</span>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Amount *</label>
          <div className="input-with-currency">
            <div className="input-field">
              <input
                {...register('amount')}
                type="text"
                className={`form-input ${errors.amount ? 'error' : ''}`}
                placeholder="0.00"
              />
              {errors.amount && <span className="field-error">{errors.amount.message as string}</span>}
            </div>
            <div className="input-field currency-field">
              <select
                {...register('currency')}
                className={`form-input ${errors.currency ? 'error' : ''}`}
              >
                <option value="">Currency</option>
                {currencies.map((c: any) => (
                  <option key={c.id} value={c.code}>{c.code}</option>
                ))}
              </select>
              {errors.currency && <span className="field-error">{errors.currency.message as string}</span>}
            </div>
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">From Wallet *</label>
          <select
            {...register('walletFromId')}
            className={`form-input ${errors.walletFromId ? 'error' : ''}`}
          >
            <option value="">Select wallet...</option>
            {filteredWallets.map((w: any) => (
              <option key={w.id} value={w.id}>{w.name} ({w.currencyCode})</option>
            ))}
          </select>
          {errors.walletFromId && <span className="field-error">{errors.walletFromId.message as string}</span>}
        </div>
        <div className="form-group">
          <label className="form-label">To Wallet *</label>
          <select
            {...register('walletToId')}
            className={`form-input ${errors.walletToId ? 'error' : ''}`}
          >
            <option value="">Select wallet...</option>
            {filteredWallets.map((w: any) => (
              <option key={w.id} value={w.id}>{w.name} ({w.currencyCode})</option>
            ))}
          </select>
          {errors.walletToId && <span className="field-error">{errors.walletToId.message as string}</span>}
        </div>
      </div>
    </>
  );
}
